import posthog from "posthog-js";

/**
 * PostHog Analytics - Frontend
 *
 * Browser-side analytics for tracking user interactions, page views, and feature usage.
 *
 * Environment Variables:
 * - VITE_POSTHOG_KEY: PostHog project key (required)
 * - VITE_POSTHOG_HOST: PostHog ingestion URL (defaults to https://n.stackverify.io managed reverse proxy)
 * - import.meta.env.DEV: Enables debug mode in development
 *
 * The default api_host routes through our managed reverse proxy at n.stackverify.io to
 * avoid ad/privacy blocklists (uBlock, EasyPrivacy, Brave) that drop direct requests
 * to *.posthog.com. See STA-273.
 *
 * ui_host is pinned to the US region PostHog Cloud UI so the toolbar, session
 * recording player, and other UI-linked features keep working when api_host is a
 * proxy. Per PostHog docs: https://posthog.com/docs/advanced/proxy
 */

// Initialize PostHog on client side only
if (typeof window !== "undefined" && import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || "https://n.stackverify.io",
    ui_host: "https://us.posthog.com",
    // Pins PostHog default-config snapshot (script injection target, session-recording strictness,
    // deferred init). Update intentionally after reviewing PostHog SDK release notes.
    defaults: "2026-01-30",
    loaded: (posthog) => {
      if (import.meta.env.DEV) {
        posthog.debug(); // Enable debug logging in development
      }
    },
    // Respect user privacy
    autocapture: false, // Disable automatic event capture for GDPR compliance
    capture_pageview: false, // We'll manually capture page views for more control
    capture_pageleave: true, // Capture page leave events for session duration
    disable_session_recording: false, // Enable session recording
  });
}

/**
 * Track a custom event
 *
 * @example
 * trackEvent('assessment_opened', { assessment_id: '123' });
 */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (globalThis.window === undefined) return;
  posthog.capture(event, properties);
}

/**
 * Track an investor opening (or acting on) a portfolio company.
 *
 * Single source of truth for the `portfolio_company_clicked` payload — used by
 * the roster row, the company card, and portfolio action-panel CTAs.
 *
 * @param entry - The portfolio company the click concerns.
 */
export function trackPortfolioCompanyClicked(entry: {
  companyTenantId: string;
  companyName: string;
  assessmentCount: number;
}): void {
  trackEvent(FrontendAnalyticsEvents.PORTFOLIO_COMPANY_CLICKED, {
    company_tenant_id: entry.companyTenantId,
    company_name: entry.companyName,
    assessment_count: entry.assessmentCount,
  });
}

/**
 * Track a page view
 *
 * Call this in Next.js route changes or page loads.
 *
 * @example
 * trackPageView('/assessments/123');
 */
export function trackPageView(
  path: string,
  properties?: Record<string, unknown>,
): void {
  if (globalThis.window === undefined) return;
  posthog.capture("$pageview", {
    $current_url: globalThis.window.location.href,
    path,
    ...properties,
  });
}

/**
 * Identify the current user
 *
 * Call this after login/signup to associate events with a user.
 *
 * @example
 * identifyUser('user-123', {
 *   email: 'user@example.com',
 *   role: 'analyst',
 *   tenant_id: 'tenant-456'
 * });
 */
export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>,
): void {
  if (globalThis.window === undefined) return;
  posthog.identify(userId, properties);
}

/**
 * Associate user with a tenant (organization)
 *
 * @example
 * setTenantGroup('tenant-123');
 */
export function setTenantGroup(tenantId: string): void {
  if (globalThis.window === undefined) return;
  posthog.group("tenant", tenantId);
}

/**
 * Reset user identity
 *
 * Call this on logout to clear user context.
 */
export function resetUser(): void {
  if (globalThis.window === undefined) return;
  posthog.reset();
}

/**
 * Check if PostHog is enabled
 */
export function isAnalyticsEnabled(): boolean {
  return globalThis.window !== undefined && !!import.meta.env.VITE_POSTHOG_KEY;
}

// Export PostHog instance for advanced usage
export { posthog };

/**
 * Frontend Analytics Events
 *
 * Client-side event names for tracking user interactions.
 */
export const FrontendAnalyticsEvents = {
  // Page Navigation
  PAGE_VIEWED: "$pageview",

  // Assessment Actions
  ASSESSMENT_OPENED: "assessment_opened",
  ASSESSMENT_LIST_VIEWED: "assessment_list_viewed",

  // Document Actions
  DOCUMENT_PREVIEW_OPENED: "document_preview_opened",
  DOCUMENT_UPLOAD_STARTED: "document_upload_started",
  DOCUMENT_UPLOAD_COMPLETED: "document_upload_completed",
  DOCUMENT_UPLOAD_FAILED_CLIENT: "document_upload_failed_client",

  // Question Actions
  QUESTION_ANSWERED: "question_answered",
  ALL_QUESTIONS_SUBMITTED: "all_questions_submitted",

  // Report Actions
  REPORT_DOWNLOADED: "report_downloaded",
  REPORT_SHARED: "report_shared",

  // Settings
  SETTINGS_OPENED: "settings_opened",
  PROFILE_UPDATED: "profile_updated",

  // Report / Analysis Actions
  ANALYSIS_DIMENSION_VIEWED: "analysis_dimension_viewed",

  // Investor Portfolio
  PORTFOLIO_COMPANY_CLICKED: "portfolio_company_clicked",
  PORTFOLIO_RISK_SEGMENT_CLICKED: "portfolio_risk_segment_clicked",
  PORTFOLIO_ACTION_ITEM_CLICKED: "portfolio_action_item_clicked",
  ADD_COMPANY_WIZARD_PATH_CHOSEN: "add_company_wizard_path_chosen",
  PIPELINE_DEAL_STATUS_VIEWED: "pipeline_deal_status_viewed",

  // Assessment scoping (sv-oa1)
  SCOPING_DESCRIBE_BOX_USED: "scoping_describe_box_used",
  SCOPING_DESCRIBE_BOX_APPLIED: "scoping_describe_box_applied",
  SCOPING_EXPRESS_PATH_USED: "scoping_express_path_used",
  SCOPING_CONTRACT_CREATED: "scoping_contract_created",

  // Errors (user-facing)
  ERROR_DISPLAYED: "error_displayed",
} as const;
